import Axios from 'axios';

/**
 *
 * @export
 * @class BaseClient
 */

class BaseClient {
  constructor(configuration, axios = Axios.create({
    baseURL: configuration == null ? void 0 : configuration.basePath,
    headers: configuration != null && configuration.apiKey ? {
      Authorization: `Bearer ${configuration.apiKey}`
    } : undefined
  })) {
    this.axios = axios;

    if (configuration) {
      this.configuration = configuration;
    }
  }

  request(options) {
    return this.axios.request(options);
  }

  get(endpoint, options = {}) {
    return this.request({ ...options,
      method: 'GET',
      url: endpoint
    });
  }

  post(endpoint, data, options = {}) {
    return this.request({ ...options,
      method: 'POST',
      url: endpoint,
      data
    });
  }

  put(endpoint, data, options = {}) {
    return this.request({ ...options,
      method: 'PUT',
      url: endpoint,
      data
    });
  }

  patch(endpoint, data, options = {}) {
    return this.request({ ...options,
      method: 'PATCH',
      url: endpoint,
      data
    });
  }

  delete(endpoint, options = {}) {
    return this.request({ ...options,
      method: 'DELETE',
      url: endpoint
    });
  }

}

/**
 * AuthApi - factory interface
 * @export
 */
function AuthApiFactory(axios) {
  return {
    /**
     * Get the currently logged-in user
     */
    async getCurrentUser() {
      const {
        data
      } = await axios.get('/v1/users/me');
      return data;
    },

    /**
     * Update the currently logged-in user
     * @param updates Values to update
     */
    async updateCurrentUser(updates) {
      const {
        data
      } = await axios.put('/v1/users/me', updates);
      return data;
    }

  };
}

/**
 * CapsulesApi - factory interface
 * @export
 */
function CapsulesApiFactory(instance) {
  return {
    /**
     * Get all capsules for the current user
     */
    async getMyCapsules() {
      const {
        data
      } = await instance.get(`/v1/users/me/capsules`);
      return data;
    },

    /**
     * Get a capsule by ID
     * @param capsuleId ID of capsule to retrieve
     */
    async getCapsule(capsuleId) {
      const {
        data
      } = await instance.get(`/v2/capsules/${capsuleId}`);
      return data;
    },

    /**
     * Get the views of a capsule by ID
     * @param capsuleId ID of capsule to retrieve
     */
    async getCapsuleViews(capsuleId) {
      const {
        data
      } = await instance.get(`/v2/capsules/${capsuleId}/views`);
      return data;
    },

    /**
     * Get all of the direct child capsules of the given capsule.
     * @param capsuleId ID of capsule to retrieve children for
     */
    async getSubCapsules(capsuleId) {
      const {
        data
      } = await instance.get(`/v2/capsules/${capsuleId}/subcapsules`);
      return data;
    },

    /**
     * Create a new capsule in the provided exhibit.
     * @param exhibitId Exhibit to create capsule in
     * @param capsule Capsule to set on creation
     */
    async createCapsule(exhibitId, capsule) {
      const {
        data
      } = await instance.post(`/v1/exhibits/${exhibitId}/capsules`, capsule);
      return data;
    },

    /**
     * Update a capsule.
     * @param capsuleId ID of capsule to update
     * @param capsule Capsule updates
     */
    async updateCapsule(capsuleId, capsule) {
      const {
        data
      } = await instance.put(`/v2/capsules/${capsuleId}`, capsule);
      return data;
    },

    /**
     * Delete the specified capsule.
     * @param capsuleId ID of capsule to delete
     */
    async deleteCapsule(capsuleId) {
      const {
        data
      } = await instance.delete(`/v2/capsules/${capsuleId}`);
      return data;
    }

  };
}

/**
 * CommentsApi - factory interface
 * @export
 */
function CommentsApiFactory(instance) {
  return {
    /**
     * Get the comments for a post.
     * @param commentListId ID of comment list for post
     */
    async getPostComments(commentListId) {
      const {
        data
      } = await instance.get(`/v1/commentLists/${commentListId}/comments`);
      return data;
    },

    /**
     * Create a new comment on a post.
     * @param commentListId ID of comment list for post
     * @param comment The comment to create
     */
    async createPostComment(commentListId, comment) {
      const {
        data
      } = await instance.post(`/v1/commentLists/${commentListId}/comments`, comment);
      return data;
    }

  };
}

/**
 * EventsApi - factory interface
 * @export
 */
function EventsApiFactory(instance) {
  return {
    /**
     * Create a new analytics event.
     * @param event The event to create
     */
    async createEvent(event) {
      const {
        data
      } = await instance.post('/v1/events', event);
      return data;
    }

  };
}

function serializePost({
  editorContent,
  content,
  ...rest
}) {
  try {
    const serializedPost = { ...rest,
      editorContent: editorContent !== undefined && typeof editorContent === 'object' ? JSON.stringify(editorContent) : editorContent,
      content: content !== undefined && typeof content === 'object' ? JSON.stringify(content) : content
    };
    return serializedPost;
  } catch (e) {
    return { ...rest
    };
  }
}

function deserializePost({
  editorContent,
  content,
  ...rest
}) {
  try {
    const newPost = { ...rest,
      editorContent: editorContent !== undefined && typeof editorContent === 'string' ? JSON.parse(editorContent) : editorContent,
      content: content !== undefined && typeof content === 'string' ? JSON.parse(content) : content
    };
    return newPost;
  } catch (e) {
    return { ...rest,
      content: null,
      editorContent: null
    };
  }
}
/**
 * PostsApi - factory interface
 * @export
 */

function PostsApiFactory(instance) {
  return {
    /**
     * Get the specified post.
     * @param id ID of post
     */
    async getPost(id) {
      const {
        data
      } = await instance.get(`/v1/posts/${id}`);
      const result = deserializePost(data);
      return result;
    },

    /**
     * Get the views of the specified post.
     * @param id ID of post
     */
    async getPostViews(id) {
      const {
        data
      } = await instance.get(`/v1/posts/${id}/views`);
      return data;
    },

    /**
     * Get all posts that are direct children of the specified capsule.
     * @param capsuleId ID of capsule
     */
    async getPostsByCapsule(capsuleId) {
      const {
        data
      } = await instance.get(`/v1/capsules/${capsuleId}/posts`);
      const result = data.map(deserializePost);
      return result;
    },

    /**
     * Get all posts for the currently logged-in user.
     */
    async getPostsByCurrentUser() {
      const {
        data
      } = await instance.get(`/v1/users/me/posts`);
      const result = data.map(deserializePost);
      return result;
    },

    /**
     * Create a new post in the given exhibit.
     * @param exhibitId ID of exhibit to create post in
     * @param post Post to create
     */
    async createPost(exhibitId, post) {
      const {
        data
      } = await instance.post(`/v1/exhibits/${exhibitId}/posts`, post);
      return data;
    },

    /**
     * Create a new post using a template in the given exhibit.
     * @param exhibitId ID of exhibit to create post in
     * @param post Post to create
     */
    async createPostTemplate(exhibitId, post) {
      const serializedPost = serializePost(post);
      const {
        data
      } = await instance.post(`/v1/exhibits/${exhibitId}/posts`, serializedPost);
      const result = deserializePost(data);
      return result;
    },

    /**
     * Update the specified post.
     * @param id ID of post
     * @param updates Updates to apply to post
     */
    async updatePost(id, updates) {
      const serializedPost = serializePost(updates);
      const {
        data
      } = await instance.put(`/v1/posts/${id}`, serializedPost);
      const result = deserializePost(data);
      return result;
    },

    /**
     * Delete the specified post.
     * @param id ID of post
     */
    async deletePost(id) {
      const {
        data
      } = await instance.delete(`/v1/posts/${id}`);
      return data;
    }

  };
}

function serializeExhibit({
  theme,
  ...rest
}) {
  try {
    const result = { ...rest,
      theme: theme !== undefined && typeof theme === 'object' ? JSON.stringify(theme) : theme
    };
    return result;
  } catch (e) {
    return { ...rest
    };
  }
}

function deserializeExhibit({
  theme,
  ...rest
}) {
  try {
    const result = { ...rest,
      theme: theme !== undefined && typeof theme === 'string' ? JSON.parse(theme) : theme
    };
    return result;
  } catch (e) {
    return { ...rest,
      theme: {
        color: 'gray'
      }
    };
  }
}
/**
 * ExhibitsApi - factory interface
 * @export
 */


function ExhibitsApiFactory(instance) {
  return {
    /**
     * Get the specified exhibit.
     * @param exhibitId ID of exhibit to get
     */
    async getExhibit(exhibitId) {
      const {
        data
      } = await instance.get(`/v1/exhibits/${exhibitId}`);
      if (data.theme) data.theme = JSON.parse(data.theme);
      return data;
    },

    /**
     * Update the specified exhibit.
     * @param exhibitId ID of exhibit to update
     * @param updates The updates to perform
     */
    async updateExhibit(exhibitId, updates) {
      const payload = serializeExhibit(updates);
      const {
        data
      } = await instance.patch(`/v1/exhibits/${exhibitId}`, payload);
      const exhibit = deserializeExhibit(data);
      return exhibit;
    },

    /**
     * Get all exhibits for the currently logged-in user.
     */
    async getMyExhibits() {
      var _data$exhibits;

      const {
        data
      } = await instance.get(`/v1/users/me`);
      const exhibits = (_data$exhibits = data.exhibits) == null ? void 0 : _data$exhibits.map(exhibit => deserializeExhibit(exhibit));
      return exhibits;
    },

    /**
     * Get all the descendant capsules of an exhibit.
     * @param exhibitId ID of exhibit
     */
    async getDescendantCapsules(exhibitId) {
      const {
        data
      } = await instance.get(`/v1/exhibits/${exhibitId}/capsules`);
      return data;
    },

    /**
     * Get all the descendant posts of an exhibit.
     * @param exhibitId ID of exhibit
     */
    async getDescendantPosts(exhibitId) {
      const {
        data
      } = await instance.get(`/v1/exhibits/${exhibitId}/posts`);
      return data.map(post => deserializePost(post));
    }

  };
}

/**
 * MediaApi - factory interface
 * @export
 */

function MediaApiFactory(instance) {
  return {
    /**
     * Create a new media object to upload.
     * @param file File to upload
     */
    async create(file) {
      const {
        name,
        size,
        type: contentType
      } = file;
      const {
        data
      } = await instance.post(`/v1/media`, {
        size,
        contentType,
        name
      });
      return data;
    },

    /**
     * Create a new media object to upload.
     *
     * @param file File to upload
     * @param attachId The ID of the object to attach the media to (ex. post or capsule)
     * @param attachType The type of object that `attachId` refers to
     *
     * @deprecated Use {@link create} function instead.
     */
    async createMedia(file, attachId, attachType) {
      const {
        name,
        size,
        type: contentType
      } = file;
      const {
        data
      } = await instance.post(attachType === 'collection' ? `/v1/capsules/${attachId}/media` : `/v1/posts/${attachId}/media`, {
        size,
        contentType,
        name
      });
      return data;
    },

    /**
     * Upload a file to the specified media URL.
     * @param options Upload options
     */
    async upload({
      url,
      file,
      onUploadProgress = () => {}
    }) {
      const {
        size,
        type: contentType
      } = file;
      await Axios.put(url, file, {
        headers: {
          'Content-Size': size,
          'Content-Type': contentType
        },
        onUploadProgress
      });
    },

    /**
     * Get the specified media.
     * @param id ID of media
     */
    async getMedia(id) {
      const {
        data
      } = await instance.get(`/v1/media/${id}?format=json`);
      return data;
    },

    /**
     * Delete the specified media.
     * @param id ID of media
     */
    async deleteMedia(id) {
      await instance.delete(`/v1/media/${id}`);
      return;
    },

    /**
     * Get all media associated with the given post.
     * @param postId ID of post
     */
    async getPostMedia(postId) {
      const {
        data
      } = await instance.get(`/v1/posts/${postId}/media`);
      return data;
    }

  };
}

/**
 * PaymentsApi - factory interface
 * @export
 */
function PaymentsApiFactory(instance) {
  return {
    /**
     * Get payment methods for the currently logged-in user.
     */
    async getPaymentMethods() {
      const {
        data
      } = await instance.get('/v1/users/me/customer/paymentMethods');
      return data;
    },

    /**
     * Create a new payment method for the currently logged-in user.
     * @param paymentMethodId ID of payment method
     */
    async createPaymentMethod(paymentMethodId) {
      const {
        data
      } = await instance.post('/v1/users/me/customer/paymentMethods', {
        paymentMethodId
      });
      return data;
    },

    /**
     * Delete the specified payment method for the currently logged-in user.
     * @param paymentMethodId ID of payment method
     */
    async deletePaymentMethod(paymentMethodId) {
      const {
        data
      } = await instance.delete(`/v1/users/me/customer/paymentMethods/${paymentMethodId}`);
      return data;
    }

  };
}

/**
 * SearchApi - factory interface
 * @export
 */
function SearchApiFactory(instance) {
  return {
    /**
     * Search for users on Treasured.
     * @param query Query string
     */
    async searchUsers(query) {
      const {
        data
      } = await instance.get(`/v1/users?searchBy=${query}`);
      return data;
    },

    /**
     * Search for users and content on Treasured.
     * @param query Query string
     * @param filters Filters to apply to search query
     */
    async search(query, filters) {
      const filterString = filters.length === 0 ? '' : `&filter=${filters.join(',')}`;
      const {
        data
      } = await instance.get(`/v1/search?query=${query}${filterString}`);
      return data;
    }

  };
}

/**
 * SlotsApi - factory interface
 * @export
 */
function SlotsApiFactory(instance) {
  return {
    /**
     * Get the slots for the given world.
     * @param worldId ID of world
     */
    async getSlots(worldId) {
      const {
        data
      } = await instance.get(`/v1/worlds/${worldId}/slots`);
      return data;
    },

    /**
     * Create a new slot in the given world.
     * @param worldId ID of world
     * @param slotId ID of slot (as defined in game)
     * @param postId Optional. ID of post
     */
    async createSlot(worldId, slotId, postId) {
      const {
        data
      } = await instance.post(`/v1/worlds/${worldId}/slots`, {
        id: slotId,
        postId
      });
      return data;
    },

    /**
     * Update the slot in the specified world.
     * @param worldId ID of world
     * @param slotId ID of slot (as defined in game)
     * @param postId ID of post
     */
    async updateSlot(worldId, slotId, postId) {
      const {
        data
      } = await instance.put(`/v1/worlds/${worldId}/slots/${slotId}`, {
        postId
      });
      return data;
    }

  };
}

/**
 * UsersApi - factory interface
 * @export
 */
function UsersApiFactory(instance) {
  return {
    /**
     * Get the specified user.
     * @param id ID of user
     */
    async getUser(id) {
      const {
        data
      } = await instance.get(`/v1/users/${id}`); // Deserialize exhibit theme

      if (data.exhibits) {
        for (let i = 0; i < data.exhibits.length; i++) {
          if (!data.exhibits[i].theme || typeof data.exhibits[i].theme === 'object') continue;
          data.exhibits[i].theme = JSON.parse(data.exhibits[i].theme);
        }
      }

      return data;
    }

  };
}

/**
 * UserPreferencesApi - factory interface
 * @export
 */
function UserPreferencesApiFactory(instance) {
  return {
    /**
     * Get all user preferences for the currently logged-in user.
     */
    async getUserPreferences() {
      const {
        data
      } = await instance.get('/v1/users/me/preferences');
      return data;
    },

    /**
     * Update the currently logged-in user's preferences.
     * @param preferences Preferences to update
     */
    async updateUserPreferences(preferences) {
      const {
        data
      } = await instance.patch('/v1/users/me/preferences', preferences);
      return data;
    }

  };
}

/**
 * WorldsApi - factory interface
 * @export
 */
function WorldsApiFactory(instance) {
  return {
    /**
     * Get all worlds for the currently logged-in user.
     */
    async getWorlds() {
      const {
        data
      } = await instance.get('/v1/users/me/worlds');
      return data;
    },

    /**
     * Get worlds shared with the currently logged-in user.
     */
    async getSharedWorlds() {
      const {
        data
      } = await instance.get('/v1/users/me/worlds?type=shared');
      return data;
    },

    /**
     * Get the specified world.
     * @param id ID of world
     */
    async getWorld(id) {
      const {
        data
      } = await instance.get(`/v2/worlds/${id}`);
      return data;
    },

    /**
     * Create a new world for the currently logged-in user.
     * @param world World to create
     * @param coupon Optional. Coupon code to apply on checkout
     */
    async createWorld(world = {}, coupon = '') {
      const {
        data
      } = await instance.post('/v2/worlds', {
        world,
        coupon
      });
      return data;
    },

    /**
     * Update the specified world.
     * @param id ID of world
     * @param updates Updates to apply to world
     */
    async updateWorld(id, updates) {
      const {
        data
      } = await instance.put(`/v2/worlds/${id}`, updates);
      return data;
    },

    /**
     * Delete the specified world.
     * @param id ID of world
     */
    async deleteWorld(id) {
      await instance.delete(`/v2/worlds/${id}`);
    },

    /**
     * Get all posts for the given world.
     * @param worldId ID of world
     */
    async getWorldPosts(worldId) {
      const {
        data
      } = await instance.get(`/v2/worlds/${worldId}/slots/posts`);
      return data;
    },

    /**
     * Get all members of the specified world.
     * @param worldId ID of world
     */
    async getWorldMembers(worldId) {
      const {
        data
      } = await instance.get(`/v2/worlds/${worldId}/members`);
      return data;
    },

    /**
     * Update the members list of the specified world.
     * @param id ID of world
     * @param action Specify whether to add or delete user
     * @param worldMembers List of world members
     */
    async updateWorldMembers(id, action, worldMembers) {
      await instance.patch(`/v2/worlds/${id}/members`, {
        action,
        worldMembers
      });
    },

    /**
     * Add members to the specified world.
     * @param worldId ID of world
     * @param worldMembers List of world members
     */
    async addWorldMembers(worldId, worldMembers) {
      return await this.updateWorldMembers(worldId, 'add', worldMembers);
    },

    /**
     * Remove members from the specified world.
     * @param worldId ID of world
     * @param worldMembers List of world members
     */
    async removeWorldMembers(worldId, worldMembers) {
      return await this.updateWorldMembers(worldId, 'delete', worldMembers);
    }

  };
}

class Client extends BaseClient {
  constructor() {
    super(...arguments);
    this.auth = AuthApiFactory(this.axios);
    this.capsules = CapsulesApiFactory(this.axios);
    this.comments = CommentsApiFactory(this.axios);
    this.events = EventsApiFactory(this.axios);
    this.exhibits = ExhibitsApiFactory(this.axios);
    this.media = MediaApiFactory(this.axios);
    this.payments = PaymentsApiFactory(this.axios);
    this.posts = PostsApiFactory(this.axios);
    this.search = SearchApiFactory(this.axios);
    this.slots = SlotsApiFactory(this.axios);
    this.users = UsersApiFactory(this.axios);
    this.userPreferences = UserPreferencesApiFactory(this.axios);
    this.worlds = WorldsApiFactory(this.axios);
  }

}

export { Client };
//# sourceMappingURL=api-client.esm.js.map
